export type GlobalPortalNavigationKey =
  | "portal"
  | "team"
  | "clients"
  | "templates"
  | "homework"
  | "coping-skills"
  | "caseload-engagement"
  | "homework-overview"
  | "completion-by-device"
  | "value-engagement"
  | "clinics"
  | "therapists"
  | "admin-clients"
  | "activity-registry"
  | "lists-of-values";

export type ClientPrimaryNavigationKey =
  | "profile"
  | "current-homework"
  | "homework-history"
  | "sessions"
  | "treatment"
  | "insights";

export type ClientSecondaryNavigationKey =
  | "values"
  | "personalized-coping-skills"
  | "plans-documents"
  | "expectancies"
  | "usage-engagement"
  | "practice-calendar"
  | "clinical-trends"
  | "progress-reports";

export type TeamNavigationKey = "overview" | "clients" | "homework" | "reports";

export interface GlobalNavigationItem {
  key: GlobalPortalNavigationKey;
  label: string;
  path: string;
}

export interface ClientNavigationItem {
  key: ClientPrimaryNavigationKey;
  label: string;
  path: (patientId: string, source?: "my-clients" | "team") => string;
}

export interface SecondaryNavigationItem {
  key: ClientSecondaryNavigationKey;
  label: string;
  path: (patientId: string, source?: "my-clients" | "team") => string;
}

export interface TeamNavigationItem {
  key: TeamNavigationKey;
  label: string;
  path: string;
}

const clientPath = (patientId: string, suffix: string) =>
  `/clinician-portal/clients/${encodeURIComponent(patientId)}/${suffix}`;

export const clientWorkspacePath = (
  patientId: string,
  suffix: string,
  source: "my-clients" | "team" = "my-clients",
) =>
  source === "team"
    ? `${teamClientDetailPath(patientId)}/${suffix}`
    : clientPath(patientId, suffix);

export const addClientPath = "/clinician-portal/clients/new";

export const teamOverviewPath = "/clinician-portal/team";

export const teamClientsPath = "/clinician-portal/team/clients";

export const teamHomeworkPath = "/clinician-portal/team/homework";

export const teamClientDetailPath = (patientId: string) =>
  `${teamClientsPath}/${encodeURIComponent(patientId)}`;

export const teamHomeworkDetailPath = (
  patientId: string,
  homeworkAssignmentId: string,
) =>
  `${teamHomeworkPath}/${encodeURIComponent(patientId)}/${encodeURIComponent(
    homeworkAssignmentId,
  )}`;

export const teamReportsPath = "/clinician-portal/team/reports";

export const addActivityTemplatePath =
  "/clinician-portal/activity-templates/new";

export const activityTemplateOverviewPath =
  "/clinician-portal/activity-templates/overview";

export const addHomeworkActivityPath =
  "/clinician-portal/homework-activities/new";

export const clientProfilePath = (patientId: string) =>
  clientPath(patientId, "profile");

export const editClientPath = (patientId: string) =>
  clientPath(patientId, "edit");

export const clientHomeworkDetailPath = (
  patientId: string,
  homeworkAssignmentId: string,
  source: "current-homework" | "homework-history" = "current-homework",
) =>
  clientPath(
    patientId,
    `${source}/${encodeURIComponent(homeworkAssignmentId)}`,
  );

export const activityTemplateDetailPath = (templateId: string) =>
  `/clinician-portal/activity-templates/${encodeURIComponent(templateId)}`;

export const activityTemplateEditPath = (templateId: string) =>
  `${activityTemplateDetailPath(templateId)}/edit`;

export const lovCatalogDetailPath = (catalogKey: string) =>
  `/clinician-portal/admin/lists-of-values/${encodeURIComponent(catalogKey)}`;

export const homeworkActivityDetailPath = (
  patientId: string,
  homeworkAssignmentId: string,
) =>
  `/clinician-portal/homework-activities/${encodeURIComponent(
    patientId,
  )}/${encodeURIComponent(homeworkAssignmentId)}`;

export const homeworkDetailEditPath = (detailPath: string) =>
  `${detailPath}${detailPath.includes("?") ? "&" : "?"}edit=1`;

export const globalPortalNavigation: readonly GlobalNavigationItem[] = [
  { key: "portal", label: "Home", path: "/clinician-portal" },
  { key: "clients", label: "Clients", path: "/clinician-portal/clients" },
  {
    key: "homework",
    label: "Homework Activities",
    path: "/clinician-portal/homework-activities",
  },
  {
    key: "templates",
    label: "Activity Templates",
    path: "/clinician-portal/activity-templates",
  },
  {
    key: "coping-skills",
    label: "Coping Skills",
    path: "/clinician-portal/coping-skills",
  },
] as const;

export const supervisorPortalNavigation: readonly GlobalNavigationItem[] = [
  { key: "team", label: "Team", path: teamOverviewPath },
] as const;

export const teamNavigation: readonly TeamNavigationItem[] = [
  { key: "overview", label: "Overview", path: teamOverviewPath },
  { key: "clients", label: "Clients", path: teamClientsPath },
  {
    key: "homework",
    label: "Homework Activities",
    path: teamHomeworkPath,
  },
  { key: "reports", label: "Reports", path: teamReportsPath },
] as const;

export const adminPortalNavigation: readonly GlobalNavigationItem[] = [
  {
    key: "clinics",
    label: "Clinics",
    path: "/clinician-portal/admin/clinics",
  },
  {
    key: "therapists",
    label: "Therapists",
    path: "/clinician-portal/admin/therapists",
  },
  {
    key: "admin-clients",
    label: "Clients",
    path: "/clinician-portal/admin/clients",
  },
  {
    key: "activity-registry",
    label: "Activity Registry",
    path: "/clinician-portal/admin/activity-registry",
  },
  {
    key: "lists-of-values",
    label: "Lists of Values",
    path: "/clinician-portal/admin/lists-of-values",
  },
] as const;

export const insightsPortalNavigation: readonly GlobalNavigationItem[] = [
  {
    key: "caseload-engagement",
    label: "Caseload Insights",
    path: "/clinician-portal/caseload-engagement",
  },
  {
    key: "homework-overview",
    label: "Homework Overview",
    path: "/clinician-portal/homework-overview",
  },
  {
    key: "value-engagement",
    label: "Value & Engagement",
    path: "/clinician-portal/value-engagement",
  },
  {
    key: "completion-by-device",
    label: "Completion by Device",
    path: "/clinician-portal/completion-by-device",
  },
] as const;

export const clientPrimaryNavigation: readonly ClientNavigationItem[] = [
  {
    key: "profile",
    label: "Client Profile",
    path: (patientId, source) => clientWorkspacePath(patientId, "profile", source),
  },
  {
    key: "current-homework",
    label: "Current Homework",
    path: (patientId, source) =>
      clientWorkspacePath(patientId, "current-homework", source),
  },
  {
    key: "homework-history",
    label: "Homework History",
    path: (patientId, source) =>
      clientWorkspacePath(patientId, "homework-history", source),
  },
  {
    key: "sessions",
    label: "Sessions",
    path: (patientId, source) => clientWorkspacePath(patientId, "sessions", source),
  },
  {
    key: "treatment",
    label: "Treatment",
    path: (patientId, source) =>
      clientWorkspacePath(patientId, "treatment/plans-documents", source),
  },
  {
    key: "insights",
    label: "Insights",
    path: (patientId, source) =>
      clientWorkspacePath(patientId, "insights/usage-engagement", source),
  },
] as const;

export const treatmentNavigation: readonly SecondaryNavigationItem[] = [
  {
    key: "plans-documents",
    label: "Plans & Documents",
    path: (patientId, source) =>
      clientWorkspacePath(patientId, "treatment/plans-documents", source),
  },
  {
    key: "values",
    label: "Values",
    path: (patientId, source) =>
      clientWorkspacePath(patientId, "treatment/values", source),
  },
  {
    key: "personalized-coping-skills",
    label: "Personalized Coping Skills",
    path: (patientId, source) =>
      clientWorkspacePath(patientId, "treatment/personalized-coping-skills", source),
  },
  {
    key: "expectancies",
    label: "Expectancies",
    path: (patientId, source) =>
      clientWorkspacePath(patientId, "treatment/expectancies", source),
  },
] as const;

export const insightsNavigation: readonly SecondaryNavigationItem[] = [
  {
    key: "usage-engagement",
    label: "Usage & Engagement",
    path: (patientId, source) =>
      clientWorkspacePath(patientId, "insights/usage-engagement", source),
  },
  {
    key: "practice-calendar",
    label: "Practice Calendar",
    path: (patientId, source) =>
      clientWorkspacePath(patientId, "insights/practice-calendar", source),
  },
  {
    key: "clinical-trends",
    label: "Clinical Trends",
    path: (patientId, source) =>
      clientWorkspacePath(patientId, "insights/clinical-trends", source),
  },
  {
    key: "progress-reports",
    label: "Progress Reports",
    path: (patientId, source) =>
      clientWorkspacePath(patientId, "insights/progress-reports", source),
  },
] as const;

export const plansAndDocumentsSections = [
  "Exposure Hierarchy",
  "Safety Plan",
  "Relapse Prevention Plan",
  "Cope Ahead Plan",
] as const;
